import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  Eye,
  Users,
  RefreshCw,
  Download,
  Calendar,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Phone,
  Mail,
  MessageSquare,
  Globe,
  TrendingUp,
  CheckCircle2,
  Clock,
  Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { campaignLeadsAPI } from '../../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICE_LABELS = {
  higher_education:       'Higher Education',
  university_application: 'University Application',
  visa_support:           'Visa Support',
  flight_ticket:          'Flight Ticket',
  accommodation:          'Accommodation',
  travel_support:         'Travel Support',
  job_support:            'Job Support',
  partner:                'Partner',
};

const SERVICE_ICONS = {
  higher_education:       '🎓',
  university_application: '🏛️',
  visa_support:           '📋',
  flight_ticket:          '✈️',
  accommodation:          '🏠',
  travel_support:         '🌏',
  job_support:            '💼',
  partner:                '🤝',
};

const STATUS_CONFIG = {
  new:         { label: 'New',         color: 'bg-blue-100 text-blue-700 border-blue-200' },
  contacted:   { label: 'Contacted',   color: 'bg-amber-100 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  converted:   { label: 'Converted',   color: 'bg-green-100 text-green-700 border-green-200' },
  closed:      { label: 'Closed',      color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useDebounce(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

function LeadDetailDrawer({ lead, onClose, onStatusChange }) {
  const [status, setStatus] = useState(lead?.status || 'new');
  const [adminNotes, setAdminNotes] = useState(lead?.adminNotes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setStatus(lead.status || 'new');
      setAdminNotes(lead.adminNotes || '');
    }
  }, [lead]);

  if (!lead) return null;

  const ci = lead.contactInfo || {};
  const sd = lead.serviceData || {};

  const handleSave = async () => {
    setSaving(true);
    try {
      await campaignLeadsAPI.updateLead(lead._id, { status, adminNotes });
      toast.success('Lead updated successfully');
      onStatusChange(lead._id, status, adminNotes);
      onClose();
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  // Flatten serviceData into readable key-value pairs
  const sdEntries = Object.entries(sd).filter(([, v]) => v);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div>
            <h2 className="text-lg font-bold text-white">Lead Details</h2>
            <p className="text-blue-200 text-xs mt-0.5">
              Submitted {lead.createdAt ? formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true }) : '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Contact Info */}
          <section>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Contact Information</h3>
            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {ci.fullName?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{ci.fullName || '—'}</p>
                  <p className="text-xs text-gray-500">Lead ID: {lead._id?.slice(-8)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <InfoRow icon={<Phone className="w-3.5 h-3.5" />} label="Phone" value={ci.phone} />
                <InfoRow icon={<Mail className="w-3.5 h-3.5" />}  label="Email" value={ci.email} />
                <InfoRow icon={<MessageSquare className="w-3.5 h-3.5" />} label="Contact Mode" value={ci.contactMode} />
                <InfoRow icon={<Clock className="w-3.5 h-3.5" />} label="Preferred Time" value={`${ci.contactTime || '—'} · ${ci.contactWhen || '—'}`} />
                <InfoRow icon={<Globe className="w-3.5 h-3.5" />} label="Heard About Us" value={ci.knowUs} />
              </div>
              {ci.notes && (
                <div className="pt-1 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1 font-medium">Notes from lead</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{ci.notes}</p>
                </div>
              )}
            </div>
          </section>

          {/* Service Info */}
          <section>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Service Details</h3>
            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{SERVICE_ICONS[lead.serviceType] || '📌'}</span>
                <div>
                  <p className="font-semibold text-gray-900">{SERVICE_LABELS[lead.serviceType] || lead.serviceType}</p>
                  <p className="text-xs text-gray-500">Service Category</p>
                </div>
              </div>
              {sdEntries.length > 0 && (
                <div className="pt-2 border-t border-gray-200 grid grid-cols-2 gap-x-4 gap-y-2">
                  {sdEntries.map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs text-gray-400 capitalize">{key.replace(/_/g, ' ').replace(/^he /, '')}</p>
                      <p className="text-sm font-medium text-gray-700">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* UTM / Tracking */}
          {(lead.utmSource || lead.utmCampaign || lead.utmMedium) && (
            <section>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Campaign Tracking</h3>
              <div className="bg-gray-50 rounded-2xl p-4 grid grid-cols-2 gap-x-4 gap-y-2">
                {lead.utmSource   && <InfoCell label="Source"   value={lead.utmSource} />}
                {lead.utmCampaign && <InfoCell label="Campaign" value={lead.utmCampaign} />}
                {lead.utmMedium   && <InfoCell label="Medium"   value={lead.utmMedium} />}
                {lead.utmContent  && <InfoCell label="Content"  value={lead.utmContent} />}
                <InfoCell label="Source Type" value={lead.source} />
                {lead.createdAt && (
                  <InfoCell label="Submitted At" value={format(new Date(lead.createdAt), 'dd MMM yyyy, hh:mm a')} />
                )}
              </div>
            </section>
          )}

          {/* Status Update */}
          <section>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Update Status</h3>
            <div className="space-y-3">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full rounded-xl border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <textarea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                rows={3}
                placeholder="Internal admin notes (not visible to lead)..."
                className="w-full rounded-xl border border-gray-200 px-3.5 py-3 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none transition-colors"
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </motion.div>
    </>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-blue-500 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <span className="text-xs text-gray-400">{label}: </span>
        <span className="text-sm font-medium text-gray-700">{value || '—'}</span>
      </div>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-700 break-all">{value || '—'}</p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CampaignLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [categoryCounts, setCategoryCounts] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const buildParams = useCallback(() => {
    const params = { page: currentPage, limit: pageSize };
    if (statusFilter !== 'all')  params.status = statusFilter;
    if (serviceFilter !== 'all') params.serviceType = serviceFilter;
    if (debouncedSearch.trim())  params.search = debouncedSearch.trim();
    if (startDate) params.startDate = startDate;
    if (endDate)   params.endDate = endDate;
    return params;
  }, [currentPage, pageSize, statusFilter, serviceFilter, debouncedSearch, startDate, endDate]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await campaignLeadsAPI.getLeads(buildParams());
      setLeads(res.data || []);
      if (res.categoryStats) setCategoryCounts(res.categoryStats);
      if (res.pagination) {
        setTotalLeads(res.pagination.total || 0);
        setTotalPages(res.pagination.totalPages || 1);
      }
    } catch {
      toast.error('Failed to fetch campaign leads');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    if (searchTerm !== debouncedSearch) setIsSearching(true);
  }, [searchTerm, debouncedSearch]);

  // Reset to page 1 on any filter change
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, statusFilter, serviceFilter, startDate, endDate]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      delete params.page;
      delete params.limit;
      const response = await campaignLeadsAPI.exportLeads(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign-leads-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleStatusChange = (id, status, adminNotes) => {
    setLeads(prev => prev.map(l => l._id === id ? { ...l, status, adminNotes } : l));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setServiceFilter('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await campaignLeadsAPI.deleteLead(deleteTarget._id);
      setLeads(prev => prev.filter(l => l._id !== deleteTarget._id));
      setTotalLeads(prev => prev - 1);
      toast.success('Lead deleted successfully');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || serviceFilter !== 'all' || startDate || endDate;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      [1,2,3,4,'...',totalPages].forEach(p => pages.push(p));
    } else if (currentPage >= totalPages - 2) {
      [1,'...',totalPages-3,totalPages-2,totalPages-1,totalPages].forEach(p => pages.push(p));
    } else {
      [1,'...',currentPage-1,currentPage,currentPage+1,'...',totalPages].forEach(p => pages.push(p));
    }
    return pages;
  };

  // Stats derived from total
  const newLeads      = leads.filter(l => l.status === 'new').length;
  const convertedLeads = leads.filter(l => l.status === 'converted').length;

  return (
    <AdminLayout>
      <AnimatePresence>
        {selectedLead && (
          <LeadDetailDrawer
            key="drawer"
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
            onStatusChange={handleStatusChange}
          />
        )}
        {deleteTarget && (
          <motion.div
            key="delete-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 relative"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Lead</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this lead?
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  No
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              Campaign Leads
            </h1>
            <p className="text-gray-500 mt-0.5 text-sm">
              Facebook campaign leads — {totalLeads} total
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchLeads} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {exporting
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Download className="w-4 h-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </div>

        {/* ── Stats Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Leads',  value: totalLeads,      icon: <Users className="w-5 h-5 text-blue-600" />,   bg: 'bg-blue-50',   border: 'border-l-blue-500' },
            { label: 'New (this page)',       value: newLeads,        icon: <Clock className="w-5 h-5 text-amber-600" />,  bg: 'bg-amber-50',  border: 'border-l-amber-500' },
            { label: 'Converted (this page)', value: convertedLeads,  icon: <CheckCircle2 className="w-5 h-5 text-green-600" />, bg: 'bg-green-50', border: 'border-l-green-500' },
          ].map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Card className={`p-4 border-l-4 ${stat.border}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center`}>{stat.icon}</div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* ── Category Cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(SERVICE_LABELS).map(([key, label], i) => {
            const isActive = serviceFilter === key;
            const count = categoryCounts[key] ?? 0;
            return (
              <motion.button
                key={key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => {
                  setServiceFilter(isActive ? 'all' : key);
                  setCurrentPage(1);
                }}
                className={`group text-left rounded-2xl border-2 p-4 transition-all duration-150 focus:outline-none ${
                  isActive
                    ? 'border-blue-500 bg-blue-50 shadow-md shadow-blue-100'
                    : 'border-gray-100 bg-white hover:border-blue-300 hover:bg-blue-50/40 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  {/* Left — icon + name */}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xl leading-none flex-shrink-0">{SERVICE_ICONS[key]}</span>
                    <p className={`text-xs font-semibold leading-snug truncate ${isActive ? 'text-blue-700' : 'text-gray-700 group-hover:text-blue-600'}`}>
                      {label}
                    </p>
                  </div>
                  {/* Right — count */}
                  <p className={`text-xl font-extrabold leading-none flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-900'}`}>
                    {count}
                  </p>
                </div>
                {isActive && (
                  <div className="mt-2 flex items-center gap-1 text-xs font-medium text-blue-500">
                    <X className="w-3 h-3" /> clear filter
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>

        {/* ── Search & Filters ── */}
        <Card className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 rounded-xl"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {isSearching && (
                <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin" />
              )}
            </div>

            {/* Status filter */}
            <Select value={statusFilter} onValueChange={v => setStatusFilter(v)}>
              <SelectTrigger className="w-full sm:w-44 rounded-xl">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                  <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Service filter */}
            <Select value={serviceFilter} onValueChange={v => setServiceFilter(v)}>
              <SelectTrigger className="w-full sm:w-52 rounded-xl">
                <SelectValue placeholder="Service Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {Object.entries(SERVICE_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Page size */}
            <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-32 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date range */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 font-medium">Date Range:</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
              />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="text-gray-500 hover:text-gray-700">
                <X className="w-3.5 h-3.5 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          {/* Active search indicator */}
          {debouncedSearch && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-blue-50 text-blue-700 border border-blue-200">
                <Search className="w-3 h-3 mr-1" />
                "{debouncedSearch}" — {totalLeads} result{totalLeads !== 1 ? 's' : ''}
              </Badge>
            </div>
          )}
        </Card>

        {/* ── Table ── */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Users className="w-16 h-16 text-gray-200 mb-4" />
              <p className="text-base font-semibold text-gray-600">No leads found</p>
              <p className="text-sm mt-1">
                {hasActiveFilters ? 'Try adjusting your filters or search term.' : 'No campaign leads have been submitted yet.'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" className="mt-4" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Lead</th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone / Email</th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-right px-6 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {leads.map((lead, index) => {
                    const ci = lead.contactInfo || {};
                    return (
                      <motion.tr
                        key={lead._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-stone-50/80 transition-colors"
                      >
                        {/* Lead name */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                              {ci.fullName?.[0]?.toUpperCase() || '?'}
                            </div>
                            <p className="font-semibold text-gray-900 text-sm">{ci.fullName || '—'}</p>
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-700">{ci.phone || '—'}</p>
                          <p className="text-xs text-gray-400">{ci.email || '—'}</p>
                        </td>

                        {/* Service */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className="text-base">{SERVICE_ICONS[lead.serviceType] || '📌'}</span>
                            <span className="text-sm text-gray-700">{SERVICE_LABELS[lead.serviceType] || lead.serviceType}</span>
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Calendar className="w-3.5 h-3.5" />
                            {lead.createdAt
                              ? formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })
                              : '—'}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <StatusBadge status={lead.status} />
                        </td>

                        {/* Action */}
                        <td className="px-6 py-4">
                          <div className="flex justify-end items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedLead(lead)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-400"
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" />
                              View Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteTarget(lead)}
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ── Pagination ── */}
        {!loading && totalLeads > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalLeads)} of {totalLeads} leads
              {hasActiveFilters && <span className="text-blue-600 ml-1">(filtered)</span>}
            </p>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {getPageNumbers().map((page, i) =>
                page === '...' ? (
                  <span key={`e-${i}`} className="px-2 text-gray-400 text-sm">…</span>
                ) : (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className={`min-w-[36px] ${currentPage === page ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  >
                    {page}
                  </Button>
                )
              )}
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
